/**
 * Consumer Type Context
 *
 * Provides persona-based navigation, permissions, and UI configuration
 * based on the consumer type from JWT claims.
 */

import ProposalsIcon from '@mui/icons-material/Article';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CaseIcon from '@mui/icons-material/Description';
import DocumentsIcon from '@mui/icons-material/Folder';
import { ConsumerType } from '@ops-matrix/shared-types';
import type React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useConsumerAuth } from './ConsumerAuthContext';

// ============================================================================
// Types
// ============================================================================

export interface NavItem {
  path: string;
  label: string;
  icon: React.ReactElement;
  badge?: number;
}

export interface PersonaConfig {
  /** Consumer type identifier */
  type: ConsumerType;
  /** Display label for this persona */
  label: string;
  /** Portal name displayed in sidebar header */
  portalName: string;
  /** Navigation items for this persona */
  navItems: NavItem[];
  /** Dashboard title */
  dashboardTitle: string;
  /** Primary action button label */
  primaryActionLabel: string;
  /** Primary action path */
  primaryActionPath: string;
  /** Terminology for cases/records section */
  terminology: {
    /** Page title (e.g., "My Loan Applications", "My Referrals") */
    recordsTitle: string;
    /** Record singular (e.g., "loan application", "referral") */
    recordSingular: string;
    /** Record plural (e.g., "loan applications", "referrals") */
    recordPlural: string;
    /** Submit button text (e.g., "Submit New Application") */
    submitButtonText: string;
    /** Empty state title */
    emptyStateTitle: string;
    /** Empty state description */
    emptyStateDescription: string;
  };
  /** Features available to this persona */
  features: {
    canSubmitDeals: boolean;
    canViewPortfolio: boolean;
    canViewCommissions: boolean;
    canSubmitCases: boolean;
    canViewApplications: boolean;
    canViewReferrals: boolean;
    canRequestQuotes: boolean;
  };
}

interface ConsumerTypeContextValue {
  /** Current consumer type */
  consumerType: ConsumerType;
  /** Persona configuration for current type */
  config: PersonaConfig;
  /** Check if a feature is available */
  hasFeature: (feature: keyof PersonaConfig['features']) => boolean;
}

// ============================================================================
// Persona Configurations
// ============================================================================

const LOAN_APPLICANT_CONFIG: PersonaConfig = {
  type: ConsumerType.LOAN_APPLICANT,
  label: 'Borrower',
  portalName: 'Borrower Portal',
  dashboardTitle: 'My Application',
  primaryActionLabel: 'Upload Documents',
  primaryActionPath: '/documents',
  navItems: [
    { path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { path: '/applications', label: 'My Loan Applications', icon: <CaseIcon /> },
    { path: '/proposals', label: 'Proposals', icon: <ProposalsIcon /> },
    { path: '/documents', label: 'Documents', icon: <DocumentsIcon /> },
  ],
  terminology: {
    recordsTitle: 'My Loan Applications',
    recordSingular: 'loan application',
    recordPlural: 'loan applications',
    submitButtonText: 'Submit New Application',
    emptyStateTitle: 'No loan applications found',
    emptyStateDescription:
      "You haven't submitted any loan applications yet. Click 'Submit New Application' to get started.",
  },
  features: {
    canSubmitDeals: false,
    canViewPortfolio: false,
    canViewCommissions: false,
    canSubmitCases: true,
    canViewApplications: true,
    canViewReferrals: false,
    canRequestQuotes: false,
  },
};

const END_CONSUMER_CONFIG: PersonaConfig = {
  type: ConsumerType.END_CONSUMER,
  label: 'Consumer',
  portalName: 'Consumer Portal',
  dashboardTitle: 'My Dashboard',
  primaryActionLabel: 'Submit Application',
  primaryActionPath: '/applications/new',
  navItems: [
    { path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { path: '/applications', label: 'My Applications', icon: <CaseIcon /> },
    { path: '/proposals', label: 'Proposals', icon: <ProposalsIcon /> },
    { path: '/documents', label: 'Documents', icon: <DocumentsIcon /> },
  ],
  terminology: {
    recordsTitle: 'My Applications',
    recordSingular: 'application',
    recordPlural: 'applications',
    submitButtonText: 'Submit New Application',
    emptyStateTitle: 'No applications found',
    emptyStateDescription:
      "You haven't submitted any applications yet. Click 'Submit New' to create your first application.",
  },
  features: {
    canSubmitDeals: false,
    canViewPortfolio: false,
    canViewCommissions: false,
    canSubmitCases: true,
    canViewApplications: false,
    canViewReferrals: false,
    canRequestQuotes: true,
  },
};

// Partner personas (CHANNEL_PARTNER, REFERRAL_PARTNER) use the Business Portal.
// If a partner-type JWT reaches the consumer portal, fall back to END_CONSUMER.
const PERSONA_CONFIGS: Partial<Record<ConsumerType, PersonaConfig>> &
  Record<'LOAN_APPLICANT' | 'END_CONSUMER', PersonaConfig> = {
  LOAN_APPLICANT: LOAN_APPLICANT_CONFIG,
  END_CONSUMER: END_CONSUMER_CONFIG,
};

// ============================================================================
// Context
// ============================================================================

const ConsumerTypeContext = createContext<ConsumerTypeContextValue | undefined>(undefined);

export function useConsumerType() {
  const context = useContext(ConsumerTypeContext);
  if (!context) {
    throw new Error('useConsumerType must be used within ConsumerTypeProvider');
  }
  return context;
}

interface ConsumerTypeProviderProps {
  children: React.ReactNode;
}

export function ConsumerTypeProvider({ children }: ConsumerTypeProviderProps) {
  const { consumer } = useConsumerAuth();

  const rawType: ConsumerType = consumer?.consumerType || ConsumerType.LOAN_APPLICANT;
  // Partners should use the Business Portal; fall back to END_CONSUMER if they reach here
  const consumerType: ConsumerType = rawType in PERSONA_CONFIGS ? rawType : ConsumerType.END_CONSUMER;
  const config = PERSONA_CONFIGS[consumerType as keyof typeof PERSONA_CONFIGS]!;

  const hasFeature = useMemo(() => (feature: keyof PersonaConfig['features']) => config.features[feature], [config]);

  const value: ConsumerTypeContextValue = useMemo(
    () => ({
      consumerType,
      config,
      hasFeature,
    }),
    [consumerType, config, hasFeature],
  );

  return <ConsumerTypeContext.Provider value={value}>{children}</ConsumerTypeContext.Provider>;
}

// ============================================================================
// Utility Exports
// ============================================================================

export type { ConsumerType };
export { PERSONA_CONFIGS };
